import React from 'react'
import { InputNumber } from 'antd'
import { useHistory } from 'react-router'
import classNames from 'classnames'
import Button from '@/components/button/button'
import { Column, Expaned, Row } from '@/components/layout/layout'
import FormTitle from '@/views/main/modules/goods-manage/components/form/form-title/form-title'
import Auth from '@/views/main/components/auth/auth'
import MemberHeaderActions from '../member-header-actions'

import './setting-page.scss'

interface Props {
    title: string
    children: React.ReactNode
    extra?: React.ReactNode
    append?: React.ReactNode
    className?: string
    bodyClassName?: string
    readOnly?: boolean
}

interface SettingPanelProps {
    title: string
    desc?: string
    children: React.ReactNode
    className?: string
    extra?: React.ReactNode
    wide?: boolean
}

export interface SettingPreviewItem {
    label: string
    value: React.ReactNode
    hint?: string
}

export interface SettingLinkItem {
    title: string
    desc: string
    route: string
    permission?: number
}

const SettingPage: React.FC<Props> = ({ children, extra, append, className, bodyClassName, readOnly }) => (
    <Column className={classNames('member-config-page', className, { 'member-config-page--readonly': readOnly })}>
        {extra ? <MemberHeaderActions>{extra}</MemberHeaderActions> : null}
        <Expaned className={classNames('member-config-page__body', bodyClassName)}>
            {readOnly ? (
                <div className="member-config-page__readonly-notice">
                    当前为直营分店，该会员规则由总部统一设置，当前页面仅支持查看。
                </div>
            ) : null}
            {children}
        </Expaned>
        {append}
    </Column>
)

export const SettingGrid: React.FC<{ children: React.ReactNode; className?: string }> = props => (
    <div className={classNames('member-config-page__grid', props.className)}>{props.children}</div>
)

export const SettingPanel: React.FC<SettingPanelProps> = props => (
    <section
        className={classNames('member-config-page__panel', props.className, {
            'member-config-page__panel--wide': props.wide,
        })}
    >
        <div className="member-config-page__panel-header">
            <div className="member-config-page__panel-heading">
                <FormTitle title={props.title} hasLine={false} />
                {props.desc ? <div className="member-config-page__panel-desc">{props.desc}</div> : null}
            </div>
            {props.extra ? <div className="member-config-page__panel-extra">{props.extra}</div> : null}
        </div>
        <div className="member-config-page__panel-body">{props.children}</div>
    </section>
)

export const SettingPreview: React.FC<{ items: SettingPreviewItem[]; className?: string }> = props => (
    <div className={classNames('member-config-page__preview-strip', props.className)}>
        {props.items.map(item => (
            <div className="member-config-page__preview-item" key={item.label}>
                <div className="member-config-page__preview-label">{item.label}</div>
                <div className="member-config-page__preview-value">{item.value}</div>
                {item.hint ? <div className="member-config-page__preview-hint">{item.hint}</div> : null}
            </div>
        ))}
    </div>
)

export const SettingActions: React.FC<{ children: React.ReactNode }> = props => (
    <Row className="member-config-page__actions">
        <Row className="member-config-page__actions-buttons">{props.children}</Row>
    </Row>
)

export const SettingTableContainer: React.FC<{ children: React.ReactNode; className?: string }> = props => (
    <div className={classNames('table-container', 'member-config-page__table', props.className)}>{props.children}</div>
)

type SettingInputNumberProps = React.ComponentProps<typeof InputNumber> & {
    addonBefore?: React.ReactNode
    addonAfter?: React.ReactNode
}

export const SettingInputNumber: React.FC<SettingInputNumberProps> = props => {
    const { addonBefore, addonAfter, style, ...inputProps } = props

    return (
        <Row
            className={classNames('member-config-page__number-input', {
                'member-config-page__number-input--with-before': !!addonBefore,
                'member-config-page__number-input--with-after': !!addonAfter,
            })}
            data-align-items-center
        >
            {addonBefore ? (
                <span className="member-config-page__number-input-addon member-config-page__number-input-addon--before">
                    {addonBefore}
                </span>
            ) : null}
            <InputNumber {...inputProps} style={style} />
            {addonAfter ? (
                <span className="member-config-page__number-input-addon member-config-page__number-input-addon--after">
                    {addonAfter}
                </span>
            ) : null}
        </Row>
    )
}

export const SettingLinkGrid: React.FC<{ items: SettingLinkItem[]; columns?: number }> = props => {
    const history = useHistory()
    const renderItem = (item: SettingLinkItem, clickable = true) => (
        <Button
            type="button"
            types="primary"
            key={item.title}
            className="member-config-page__link"
            onClick={clickable ? () => history.push(item.route) : undefined}
        >
            <div className="member-config-page__link-title">{item.title}</div>
            <div className="member-config-page__link-desc">{item.desc}</div>
        </Button>
    )

    return (
        <div
            className="member-config-page__link-grid"
            style={{ gridTemplateColumns: `repeat(${props.columns || props.items.length}, minmax(0, 1fr))` }}
        >
            {props.items.map(item =>
                item.permission ? (
                    <Auth key={item.title} perssion={item.permission} onClick={() => history.push(item.route)}>
                        {renderItem(item, false)}
                    </Auth>
                ) : (
                    renderItem(item)
                ),
            )}
        </div>
    )
}

export const WEEK_OPTIONS = [
    { label: '周一', value: 1 },
    { label: '周二', value: 2 },
    { label: '周三', value: 3 },
    { label: '周四', value: 4 },
    { label: '周五', value: 5 },
    { label: '周六', value: 6 },
    { label: '周日', value: 7 },
]

export const SCENE_OPTIONS = [
    { label: '收银台', value: 0 },
    { label: '商城', value: 1 },
]

export const MONTH_OPTIONS = Array.from({ length: 31 }).map((_, index) => ({
    label: `${index + 1}号`,
    value: index + 1,
}))

export const numberOrUndefined = (value: any) => {
    if (value === '' || value === undefined || value === null) return undefined

    return Number(value)
}

export const numberOrNull = (value: any) => {
    if (value === '' || value === undefined || value === null) return null

    return Number(value)
}

export const boolToStatus = (value: boolean) => (value ? 0 : -1)

export const statusToBool = (value: any) => value === true || value === 0

export default SettingPage
